import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomExpandDown from "@tCentro/Icons/CustomExpandDown";
import CustomExpandUp from "@tCentro/Icons/CustomExpandUp";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import { Namespace } from "css";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

export interface FilterTypeAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    optionsCss?: InjectableCss;
    wrapper?: InjectableCss;
    itemName?: InjectableCss;
}

export const filterTypeAccordionSectionStyles: FilterTypeAccordionSectionStyles = {
    wrapper: {
        css: css`
            border-bottom: 2px solid ${COLORS.background.grayShade};
            width: 100%;
        `,
    },
    accordionSection: {
        toggleIconProps: {
            src: CustomExpandUp,
            size: 8,
            css: css`
                > svg {
                    > g {
                        stroke-width: 0.8;
                    }
                }
            `,
        },

        expandIconProps: {
            src: CustomExpandDown,
            size: 8,
            css: css`
                > svg {
                    > g {
                        stroke-width: 0.8;
                    }
                }
            `,
        },

        headerProps: {
            css: css`
                > button {
                    background: ${COLORS.white};
                    border: none;
                    padding: 16px 9px 13px 1px !important;
                    > span {
                        font-size: ${TYPOGRAPHY.size.large};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        letter-spacing: 0;
                        line-height: 22px;
                        color: #333333;
                        @media all and (max-width: 992px) and (min-width: 0px) {
                            font-size: ${TYPOGRAPHY.size.base};
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            letter-spacing: 0;
                            line-height: 19px;
                        }
                    }
                }
            `,
        },

        panelProps: {
            css: css`
                border: none;
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
                padding: 0 15px 20px 0;
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            height: 24px;
            justify-content: center;
            &:not(:last-child) {
                margin-bottom: 5px;
            }
            > div[class^="CheckboxStyle"] {
                > label[class^="TypographyStyle"] {
                    line-height: 15px;
                }
            }
        `,
    },
    checkBox: {
        // iconProps: { src: CompareDefault },
        css: css`
            span[class^="IconWrapper"] {
                min-width: 19px;
                min-height: 19px;
                > svg {
                    height: 19px;
                    width: 19px;
                }
                &:focus {
                    box-shadow: none;
                }
            }
        `,
    },
    checkBoxSelected: {
        // iconProps: { src: CompareActive },
        css: css`
            span[class^="IconWrapper"] {
                min-width: 19px;
                min-height: 19px;
                > svg {
                    height: 19px;
                    width: 19px;
                }
                &:focus {
                    box-shadow: none;
                }
            }
        `,
    },
    seeAllLink: {
        css: css`
            padding-top: 12px;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            letter-spacing: 0;
            line-height: 17px;
            color: ${COLORS.link.base};
        `,
    },
    seeLessLink: {
        css: css`
            padding-top: 12px;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            letter-spacing: 0;
            line-height: 17px;
            color: ${COLORS.link.base};
        `,
    },

    itemName: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smallest};
            letter-spacing: 1.08px;
            line-height: 9px;
            color: #33333a;
        `,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: FilterTypeAccordionSectionStyles;
    expandByDefault: boolean;
}

const FilterTypeAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(filterTypeAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    showMoreLimit = 5;
    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;
    if (!limitedFacets) {
        return null;
    }
    return (
        <Accordion headingLevel={1} {...styles.wrapper}>
            <ManagedAccordionSection
                title={title}
                {...styles.accordionSection}
                initialExpanded={expandByDefault || anySelected}
            >
                {limitedFacets
                    ?.filter(facet => facet.name !== "" && typeof facet.name === "string")
                    ?.map(facet => (
                        <CheckboxGroup key={facet.name} {...styles.checkBoxGroup}>
                            <Checkbox
                                {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                                checked={
                                    typeof window !== 'undefined' &&
                                    window?.parent.location.href.indexOf("announcements=true") !== -1 &&
                                    facet.name === "announcement"
                                        ? true
                                        : facet.selected
                                }
                                onChange={() => onChangeFacet(facet)}
                                data-test-selector={`facetFilter${facet.name}`}
                            >
                                <Typography {...styles.itemName}>{translate("{0}", facet.name)}</Typography>
                            </Checkbox>
                        </CheckboxGroup>
                    ))}
                {facets &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                            {translate("See less")}
                        </Link>
                    ) : (
                        <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                            {translate("See more")}
                        </Link>
                    ))}
            </ManagedAccordionSection>
        </Accordion>
    );
};

export default FilterTypeAccordionSection;
